import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Modal, Input } from 'antd'
import { SearchOutlined, EnterOutlined } from '@ant-design/icons'

import { flattenNavLeaves } from './AdminSidebar'

// Global command palette: Cmd/Ctrl-Shift-P opens a fuzzy jump-to-page search over the
// entire admin navigation (all sections + group children). Keyboard-first
// (↑/↓ to move, Enter to go, Esc to close) — the enterprise standard for deep
// navigation (Stripe / Linear / GitHub).
export default function CommandPalette() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(0)
  const activeRef = useRef<HTMLDivElement>(null)

  const items = useMemo(
    () => flattenNavLeaves().map(i => ({
      path: i.path,
      label: t(i.labelKey, i.fallback),
      section: t(i.sectionLabelKey, i.sectionFallback),
    })),
    [t],
  )

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    if (!s) return items
    return items.filter(i =>
      i.label.toLowerCase().includes(s) ||
      i.section.toLowerCase().includes(s) ||
      i.path.toLowerCase().includes(s),
    )
  }, [q, items])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Strg/Cmd-K gehoert der globalen Datensatz-Suche im Header (Kunden,
      // Rechnungen, Tickets) — das ist der 90%-Fall. Die Seiten-Navigation
      // liegt auf Strg/Cmd-Shift-P, wie in VS Code/Linear. Vorher belegten
      // BEIDE Strg/Cmd-K und feuerten gleichzeitig.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault()
        setQ('')
        setSel(0)
        setOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => { setSel(0) }, [q])
  useEffect(() => { activeRef.current?.scrollIntoView({ block: 'nearest' }) }, [sel])

  const go = (path: string) => { setOpen(false); navigate(path) }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[sel]) go(filtered[sel].path) }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
  }

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closable={false}
      width={580}
      styles={{ body: { padding: 0 } }}
      destroyOnHidden
    >
      <div className="cmdk">
        <Input
          size="large"
          autoFocus
          prefix={<SearchOutlined />}
          placeholder={t('cmd.placeholder', 'Springe zu … (Seite suchen)')}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          variant="borderless"
          allowClear
        />
        <div className="cmdk-list">
          {filtered.length === 0 && (
            <div className="cmdk-empty">{t('cmd.none', 'Nichts gefunden')}</div>
          )}
          {filtered.map((i, idx) => (
            <div
              key={i.path}
              ref={idx === sel ? activeRef : undefined}
              className={`cmdk-item${idx === sel ? ' active' : ''}`}
              onMouseEnter={() => setSel(idx)}
              onClick={() => go(i.path)}
            >
              <span className="cmdk-item-label">{i.label}</span>
              <span className="cmdk-item-section">{i.section}</span>
              {idx === sel && <EnterOutlined className="cmdk-item-enter" />}
            </div>
          ))}
        </div>
        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> {t('cmd.navigate', 'navigieren')}</span>
          <span><kbd>↵</kbd> {t('cmd.openItem', 'öffnen')}</span>
          <span><kbd>Esc</kbd> {t('cmd.close', 'schließen')}</span>
        </div>
      </div>
    </Modal>
  )
}
